import { useState, useEffect, MouseEventHandler, useRef } from 'react'
import LocationProps from '../../props/LocationProps'
import { createPortal } from 'react-dom'
import { config } from '../../config'
// import TagList from '../tags/TagList'
import CouncilProps from '../../props/CouncilProps'
import './modal.scss'

type ModalProps = {
  showModal: boolean
  setUserShowModal: (flag: boolean) => void
  activeLocation: LocationProps[]
  councilList: CouncilProps
  modalTrigger: HTMLElement | null
}

const Modal = ({
  showModal = false,
  setUserShowModal,
  activeLocation,
  councilList,
  modalTrigger
}: ModalProps) => {
  const [activeView, setActiveView] = useState<string | null>(null)
  const [activeFilteredView, setactiveFileredView] = useState<LocationProps[]>([])
  const modalRef = useRef<HTMLDialogElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (showModal) {
      previousFocusRef.current = document.activeElement as HTMLElement
      const m = modalRef.current
      if (!m) return
      m.open = true
      handleBody(true)
      // Set focus to the first focusable element
      const firstFocusable = m.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') as HTMLElement
      if (firstFocusable) {
        firstFocusable.focus()
      }
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus()
    }
  }, [showModal])

  const handleFocusTrap = (e: KeyboardEvent) => {
    if (!modalRef.current || !showModal) return

    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    const firstFocusable = focusableElements[0] as HTMLElement
    const lastFocusable = focusableElements[focusableElements.length - 1] as HTMLElement

    if (e.key === 'Tab') {
      if (e.shiftKey && document.activeElement === firstFocusable) {
        e.preventDefault()
        lastFocusable.focus()
      } else if (!e.shiftKey && document.activeElement === lastFocusable) {
        e.preventDefault()
        firstFocusable.focus()
      }
    }

    if (e.key === 'Escape') {
      handleCloseModal()
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleFocusTrap)
    return () => {
      document.removeEventListener('keydown', handleFocusTrap)
    }
  }, [showModal])

  const handleCloseModal = () => {
    setActiveView(null)
    setUserShowModal(false)
    handleBody(false)
    modalTrigger?.focus()
  }

  const handleBody = (flag:boolean) => {
    document.body.classList.toggle('dialog-active', flag)
  }

  const handleSwitchClick: MouseEventHandler<HTMLAnchorElement> | undefined = (
    evt: React.MouseEvent,
  ) => {
    if (!evt) return false
    evt.preventDefault()
    const target = evt.target as HTMLElement

    if (!target?.dataset?.name) return
    setActiveView(target!.dataset?.name)

    return false //make sure we kill the link
  }

  useEffect(() => {
    const locs = activeLocation.filter(l => l.active)
    setactiveFileredView([...locs])
  }, [activeLocation])

  const hasValidCouncilData = (l: LocationProps, councilList: CouncilProps): boolean => {
    return !!(
      l?.council?.id && 
      councilList[l.council.id] && 
      councilList[l.council.id].link
    );
  };

  return createPortal(
    <dialog
      ref={modalRef}
      className={`crc-dialog nsw-dialog js-dialog js-dialog-dismiss ${showModal ? 'active' : ''}`}
      open={showModal}
      id="crc-modal"
      aria-labelledby="crc-dialog-title"
      aria-modal="true"
      role="dialog"
    >
      <div className="nsw-dialog__wrapper crc-dialog__wrapper">
        <div className="nsw-dialog__container crc-dialog__container">
          <div className={`nsw-dialog__top crc-dialog__top  ${activeFilteredView.length > 1 ? 'nsw-flex-column' : 'nsw-flex-row-reverse'}`}>
            <div className={`nsw-display-flex nsw-flex-row nsw-justify-content-between`}>
              {activeFilteredView.length > 1 && (
                <div className="nsw-tabs js-tabs crc-tabs nsw-p-bottom-xs">
                  <ul className="nsw-tabs__list crc-tabs__list">
                    {activeFilteredView.length && activeFilteredView.map((l, idx) => {
                      return (
                        <>
                          {l.active &&
                            <li>
                              <a
                              href="#"
                              className={`${config.namespace}-tab ${activeView === `tab-${l.type}--${idx}` || (idx === 0 && activeView === null) ? 'active' : ''}`}
                              data-name={`tab-${l.type}--${idx}`}
                              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => handleSwitchClick(e)}
                              >
                                {config.titleMap[l.type]}
                              </a>
                            </li>
                          }
                        </>
                      )
                    })}
                  </ul>
                </div>
              )}
              <div className="nsw-dialog__close">
                <button
                  className="nsw-icon-button js-close-dialog"
                  onKeyUp={evt => {
                    // the react library triggers the modal popup on the keydown event. This pops the modal up as expected, with the focus on the close item.
                    // but the close button click listener was triggering from the keyup event, causing the modal to close immediately.
                    // this abandons the keyup event to its fate,
                    evt.stopPropagation()
                    evt.preventDefault()
                    return false
                  }}
                  onClick={(evt) => {
                    evt.stopPropagation()
                    handleCloseModal()
                  }}
                  >
                  <span
                    className="material-icons nsw-material-icons"
                    aria-hidden="true"
                    >
                    close
                  </span>
                  <span className="sr-only">Close</span>
                </button>
              </div>
            </div>
            {activeFilteredView.length && activeFilteredView.map((l, idx) => {
              return (
                <>
                  {l.active &&
                    <div className={`nsw-dialog__title crc-dialog__title ${activeFilteredView.length > 1 ? 'nsw-p-top-xs' : ''} ${activeView === `tab-${l.type}--${idx}` || (idx === 0 && activeView === null) ? 'nsw-display-block' : 'nsw-display-none'}`}>
                      <h2 id="crc-dialog-title">{l?.title}</h2>
                    </div>
                  }
                </>
              )
            })}
          </div>
          {activeFilteredView.length && activeFilteredView.map((l, idx) => {
            return (
              <>
                {l.active &&
                  <div
                  className={`views-map ${activeView === `tab-${l.type}--${idx}` || (idx === 0 && activeView === null) ? 'nsw-display-block' : 'nsw-display-none'}`}
                  >
                    {activeFilteredView.length > 0 && l.image?.url &&
                      <figure className="nsw-media nsw-media--80 nsw-media--true crc-dialog__media">
                        <img src={l.image.url} alt={l.image.alt ?? ''} />
                      </figure>
                    }
                    {l.dates?.length &&
                    <div className=" nsw-dialog__content nsw-p-bottom-xs nsw-list-item__info nsw-display-flex">
                      <strong>Dates:</strong>
                      <div className="nsw-m-left-sm">
                      {l.dates.map(locdate => {
                        return (
                          <div className="crc-dates">
                            {locdate.start &&
                              <span>
                                {
                                  new Date(locdate.start).toDateString()
                                }
                              </span>
                            }
                            {locdate.end && locdate.start !== locdate.end &&
                              <>
                                &mdash;
                                <span>
                                  {
                                    new Date(locdate.end).toDateString()
                                  }
                                </span>
                              </>
                            }
                          </div>
                        )
                      })}
                      </div>
                    </div>
                  }
                    { l?.description &&
                        <div className="nsw-dialog__content" dangerouslySetInnerHTML={{__html: l?.description}}>
                        </div>
                      }
                    <div className="nsw-dialog__bottom crc-dialog__bottom">
                      {l?.council && hasValidCouncilData(l, councilList) && (
                        <a
                          href={`${councilList[l.council.id].link.uri}`}
                          target='_blank'
                          className="nsw-button nsw-button--dark nsw-button--primary js-close-dialog"
                        >
                          {councilList[l.council.id].link.title}
                        </a>
                      )}
                      {l?.get_directions && (
                        <a
                        href={`${l?.get_directions.uri}`}
                        target='_blank'
                        className="nsw-button nsw-button--dark-outline nsw-button--primary js-close-dialog"
                        > <span className="material-icons nsw-material-icons nsw-p-right-xs nsw-display-inline-block" aria-hidden="true">directions</span>
                          {l?.get_directions.title}
                        </a>
                      )}
                    </div>
                  </div>
                }
              </>
            )
          })}
        </div>
      </div>
    </dialog>,
    document!.querySelector('body')!,
  )
}

export default Modal
